<app-normal-navbar></app-normal-navbar>

<div class="site-page-wrapper">
  <!-- Soft Ambient Glow Layers -->
  <div class="site-ambient-bg"></div>

  <!-- Standardized Centered Container -->
  <div class="site-container">

    <!-- ═══════════════ HERO SECTION ═══════════════ -->
    <header class="hero-section">

      <h1 class="hero-title">Privacy Policy</h1>

      <p class="hero-subtitle">
        Your privacy is paramount to us at SceneTale. This comprehensive Privacy Policy outlines the types of personal
        information we collect, how it is handled, and how third-party advertising partners (including Google AdSense)
        utilize cookies on our website.
      </p>

      <div class="hero-meta-row">
        <span class="meta-pill"><strong>Effective Date:</strong> April 2026</span>
        <span class="meta-pill"><strong>Version:</strong> 2.4</span>
        <span class="meta-pill"><strong>Scope:</strong> Global (GDPR / CCPA / COPPA)</span>
      </div>
    </header>

    <!-- ═══════════════ POLICY SECTIONS ═══════════════ -->
    <div class="policy-sections-list">

      <!-- Section 1: Overview & Consent -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-emerald">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 01</span>
            <h2 class="card-title">Consent &amp; Scope</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            By accessing or using <strong>SceneTale</strong> (accessible from <a href="/"
              class="inline-link">scenetale.com</a> or our local development environments),
            you hereby consent to this Privacy Policy and agree to its terms. If you do not agree with any part of this
            policy, please discontinue the use of our services.
          </p>
          <p>
            This policy applies exclusively to our online activities and is valid for visitors to our website regarding
            the information shared and/or collected on SceneTale.
          </p>
        </div>
      </section>

      <!-- Section 2: Information We Collect -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-teal">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 02</span>
            <h2 class="card-title">Information We Collect</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            We collect the minimum amount of personal information required to deliver an engaging, secure, and
            personalized cinema experience:
          </p>
          <ul class="styled-list">
            <li>
              <strong>Account &amp; Profile Information:</strong> When you register or sign in using social
              authentication (Google, GitHub, X), we receive your verified display name, email address, and profile
              picture avatar to establish your cinephile director profile.
            </li>
            <li>
              <strong>Interactive Trivia &amp; Quiz Data:</strong> We record your quiz choices, scores, completed
              challenges, and points to rank you on global leaderboards and award collectible cinema badges.
            </li>
            <li>
              <strong>Log Files &amp; Technical Usage Data:</strong> Like standard web platforms, SceneTale uses log
              files. Information logged includes internet protocol (IP) addresses, browser type, Internet Service
              Provider (ISP), date/time stamps, referring/exit pages, and number of clicks. These are not linked to any
              personally identifiable information.
            </li>
            <li>
              <strong>Communications:</strong> If you contact us directly via our email channels or contact forms, we
              may retain your message, email address, and attachments to assist you.
            </li>
          </ul>
        </div>
      </section>

      <!-- Section 3: How We Use Your Data -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-amber">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 03</span>
            <h2 class="card-title">How We Use Your Information</h2>
          </div>
        </div>
        <div class="card-body">
          <p>We use the data collected for legitimate, transparent purposes, including:</p>
          <div class="grid-two-col">
            <div class="feature-box">
              <h4>🎯 Core Service Operation</h4>
              <p>Maintaining your active user session, saving your movie watchlist preferences, and computing
                leaderboard standings.</p>
            </div>
            <div class="feature-box">
              <h4>🛡️ Security &amp; Abuse Prevention</h4>
              <p>Detecting bot behavior, preventing fraudulent trivia attempts, and safeguarding platform integrity.</p>
            </div>
            <div class="feature-box">
              <h4>📈 Platform Optimization</h4>
              <p>Understanding which cinema categories, trailers, and blog articles cinephiles enjoy to curate higher
                quality content.</p>
            </div>
            <div class="feature-box">
              <h4>✉️ Essential Communications</h4>
              <p>Sending critical account verification, security notifications, or responding directly to user
                inquiries.</p>
            </div>
          </div>
          <p class="highlight-callout">
            <strong>Absolute Guarantee:</strong> SceneTale does not sell, rent, or lease your personal information or
            contact details to any third-party marketing companies.
          </p>
        </div>
      </section>

      <!-- Section 4: Google AdSense & DoubleClick Cookies (MANDATORY FOR ADSENSE) -->
      <section class="policy-card featured-policy-card">
        <div class="card-header-row">
          <div class="card-icon-box icon-gold">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="4"></rect>
              <line x1="8" y1="8" x2="16" y2="16"></line>
              <line x1="16" y1="8" x2="8" y2="16"></line>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag tag-gold">Crucial AdSense Disclosure</span>
            <h2 class="card-title">Google AdSense &amp; DoubleClick DART Cookies</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            SceneTale may display advertisements served by <strong>Google AdSense</strong>. Google is one of our
            third-party advertising vendors on this website.
          </p>
          <div class="legal-highlight-box">
            <h4>Google Advertising Policy Compliance Notice:</h4>
            <ul>
              <li>
                <strong>Third-Party Vendor Cookies:</strong> Third-party vendors, including Google, use cookies to serve
                advertisements based on a user's previous visits to SceneTale or other websites on the internet.
              </li>
              <li>
                <strong>DoubleClick DART Cookie:</strong> Google's use of advertising cookies (such as the DART cookie)
                enables Google and its ad exchange partners to serve personalized advertisements to users based on their
                browsing behavior on SceneTale and other sites across the web.
              </li>
              <li>
                <strong>Opting Out of Personalized Advertising:</strong> Users have the right to opt out of personalized
                advertising by visiting Google's official Ad Settings page:
                <div class="opt-out-links">
                  <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" class="badge-link">
                    🔗 Google Ads Settings (adssettings.google.com)
                  </a>
                  <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer"
                    class="badge-link">
                    🔗 AboutAds.info Consumer Choice (aboutads.info)
                  </a>
                  <a href="https://www.networkadvertising.org/managing/opt_out.asp" target="_blank"
                    rel="noopener noreferrer" class="badge-link">
                    🔗 Network Advertising Initiative (networkadvertising.org)
                  </a>
                </div>
              </li>
            </ul>
          </div>
          <p>
            Please note that SceneTale has no access to or control over cookies that are used by third-party
            advertisers. We recommend consulting the respective Privacy Policies of these third-party ad servers for
            more detailed guidance.
          </p>
        </div>
      </section>

      <!-- Section 5: Third-Party APIs & Data Sources -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-teal">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="16 18 22 12 16 6"></polyline>
              <polyline points="8 6 2 12 8 18"></polyline>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 05</span>
            <h2 class="card-title">Third-Party APIs &amp; External Services</h2>
          </div>
        </div>
        <div class="card-body">
          <p>SceneTale integrates with external trusted services to bring you cinematic metadata and security:</p>
          <ul class="styled-list">
            <li>
              <strong>The Movie Database (TMDB) API:</strong> We query the TMDB API to display movie titles, synopses,
              cast rosters, release dates, and posters. TMDB processes API requests according to their own privacy
              policy. (This product uses the TMDB API but is not endorsed or certified by TMDB).
            </li>
            <li>
              <strong>OAuth Providers (Google, GitHub, X):</strong> Used exclusively to verify your identity without
              SceneTale ever viewing or storing your passwords.
            </li>
          </ul>
        </div>
      </section>

      <!-- Section 6: CCPA Privacy Rights -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-emerald">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 8v8"></path>
              <path d="M8 12h8"></path>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 06</span>
            <h2 class="card-title">CCPA Privacy Rights (Do Not Sell My Information)</h2>
          </div>
        </div>
        <div class="card-body">
          <p>Under the California Consumer Privacy Act (CCPA), California consumers have specific rights:</p>
          <ul class="styled-list">
            <li><strong>Right to Know:</strong> Request that a business disclose the categories and specific pieces of
              personal data it has collected.</li>
            <li><strong>Right to Delete:</strong> Request that a business delete any personal data about the consumer
              that has been collected.</li>
            <li><strong>Right to Opt-Out:</strong> Request that a business that sells a consumer's personal data, not
              sell the consumer's personal data. <em>SceneTale does not sell personal data.</em></li>
          </ul>
          <p>If you submit a request, we have one month to respond to you. Please reach out via <a
              href="mailto:privacy@scenetale.com" class="inline-link">privacy&#64;scenetale.com</a>.</p>
        </div>
      </section>

      <!-- Section 7: GDPR Data Protection Rights -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-amber">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 07</span>
            <h2 class="card-title">GDPR Data Protection Rights</h2>
          </div>
        </div>
        <div class="card-body">
          <p>For visitors from the European Economic Area (EEA), you are entitled to the full protection of the General
            Data Protection Regulation (GDPR):</p>
          <div class="grid-two-col">
            <div class="feature-box">
              <h4>1. Right of Access</h4>
              <p>You can request copies of your stored personal profile data.</p>
            </div>
            <div class="feature-box">
              <h4>2. Right to Rectification</h4>
              <p>You can request correction of inaccurate or incomplete records.</p>
            </div>
            <div class="feature-box">
              <h4>3. Right to Erasure</h4>
              <p>You can request that your account and score history be permanently purged.</p>
            </div>
            <div class="feature-box">
              <h4>4. Right to Data Portability</h4>
              <p>You can request transfer of your data to another organization or directly to you.</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Section 8: Children's Information (COPPA) -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-teal">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 08</span>
            <h2 class="card-title">Children's Information (COPPA)</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            Protecting children online is especially important to us. SceneTale does not knowingly collect any Personal
            Identifiable Information from children under the age of 13.
          </p>
          <p>
            If you believe that your child has provided personal information on our website, we strongly encourage you
            to contact us immediately, and we will promptly remove such information from our records.
          </p>
        </div>
      </section>

      <!-- Section 9: Contact & Data Inquiries -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-emerald">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 09</span>
            <h2 class="card-title">Contact Our Privacy Officer</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            If you have questions regarding this Privacy Policy, wish to exercise your GDPR/CCPA rights, or have
            questions about our compliance with Google AdSense terms, please reach out to our team:
          </p>
          <div class="contact-pill-row">
            <a href="mailto:privacy@scenetale.com" class="contact-pill-link">
              <span class="pill-dot"></span>
              <strong>Privacy Inquiries:</strong> privacy&#64;scenetale.com
            </a>
            <a href="mailto:support@scenetale.com" class="contact-pill-link">
              <span class="pill-dot pill-dot--amber"></span>
              <strong>General Support:</strong> support&#64;scenetale.com
            </a>
          </div>
        </div>
      </section>

    </div>

    <!-- ═══════════════ BOTTOM TRUST BANNER ═══════════════ -->
    <section class="bottom-trust-card">
      <div class="trust-content">
        <h3>Transparency First Cinema</h3>
        <p>Built for movie enthusiasts with privacy, fairness, and safety at the foundation.</p>
        <div class="trust-actions">
          <a routerLink="/" class="btn-primary-pill">Return to Home</a>
          <a routerLink="/terms" class="btn-secondary-pill">Read Terms of Service &rarr;</a>
        </div>
      </div>
    </section>

  </div>
</div>

<app-footer></app-footer>